import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { stepVillaPreferenceSchema, type StepVillaPreferenceForm } from './schemas';
import { useRegistrationStore } from '../../store/registrationStore';
import { Button } from '../Button';

const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';
const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';

// Customer-facing framing: rooms needed in the house. Values unchanged (backend
// still stores them as villa types).
const ROOM_OPTIONS = [
  { value: 'standard-2bed', label: '2 rooms', description: 'For about 4–6 people' },
  { value: 'standard-3bed', label: '3 rooms', description: 'For about 6–8 people' },
];

interface StepVillaPreferenceProps {
  onNext: () => void;
  onBack: () => void;
}

export function StepVillaPreference({ onNext, onBack }: StepVillaPreferenceProps) {
  const { villaPreference, setVillaPreference } = useRegistrationStore();

  const { register, handleSubmit } = useForm<StepVillaPreferenceForm>({
    resolver: zodResolver(stepVillaPreferenceSchema),
    defaultValues: {
      villaTypePreference: villaPreference.villaTypePreference || '',
      flexibilityNotes: villaPreference.flexibilityNotes || '',
      roomConfigNotes: villaPreference.roomConfigNotes || '',
    },
  });

  const onSubmitStep = (data: StepVillaPreferenceForm) => {
    setVillaPreference({
      villaTypePreference: data.villaTypePreference || null,
      flexibilityNotes: data.flexibilityNotes || null,
      roomConfigNotes: data.roomConfigNotes || null,
    });
    onNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmitStep)} className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900">Room Accommodations</h2>
      <p className="text-sm text-slate-600">
        About how many rooms will your group need in the house? This helps us make sure
        there's space for everyone. Final room assignments are made by the reunion admin.
      </p>

      <div className="space-y-3">
        {ROOM_OPTIONS.map((room) => (
          <label
            key={room.value}
            className="flex items-start gap-3 border border-slate-200 rounded-lg p-4 cursor-pointer hover:border-primary transition-colors"
          >
            <input
              type="radio"
              value={room.value}
              {...register('villaTypePreference')}
              className="mt-1"
            />
            <div>
              <p className="font-medium text-slate-900">{room.label}</p>
              <p className="text-sm text-slate-500">{room.description}</p>
            </div>
          </label>
        ))}
      </div>

      <div>
        <label htmlFor="vp-flexibility" className={labelClasses}>
          Flexibility Notes
        </label>
        <textarea
          id="vp-flexibility"
          className={`${inputClasses} resize-none`}
          rows={3}
          placeholder="e.g., Open to fewer rooms if needed"
          {...register('flexibilityNotes')}
        />
      </div>

      <div>
        <label htmlFor="vp-room-config" className={labelClasses}>
          Room Configuration Preferences
        </label>
        <textarea
          id="vp-room-config"
          className={`${inputClasses} resize-none`}
          rows={3}
          placeholder="e.g., Need crib in master bedroom, kids share a room"
          {...register('roomConfigNotes')}
        />
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" variant="primary">
          Next: Special Needs
        </Button>
      </div>
    </form>
  );
}
